import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import CardTable from "../../../components/AdminTable/CardTable";
import { apiKey } from "../../../config/config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCog, FaEdit, FaTrashAlt } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { Link } from "react-router-dom";
import { languageHelper } from "../../../config/helperFunctions";

function Waste() {
  const [data, setData] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDelModal, setShowDelModal] = useState(false);
  const [selectedWaste, setSelectedWaste] = useState(null);
  const user = JSON.parse(localStorage.getItem("rms-admin"));

  // Toggle dropdown for each row
  const toggleDropdown = useCallback(
    (id) => setOpenDropdown(openDropdown === id ? null : id),
    [openDropdown]
  );

  // Open delete modal
  const openDeleteModal = (waste) => {
    setSelectedWaste(waste);
    setShowDelModal(true);
    setOpenDropdown(null);
  };

  // Fetch waste data
  const fetchWaste = useCallback(async () => {
    try {
      const res = await axios.get(`${apiKey}/api/rms/admin/waste`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      if (res.status === 200 && res.data.success) {
        const formatted = res.data.wastes.map((item, index) => ({
          SN: index + 1,
          ...item,
        }));
        setData(formatted);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  }, [user?.token]);

  useEffect(() => {
    fetchWaste();
  }, [fetchWaste]);

  // Handle delete action
  const handleDelete = async () => {
    try {
      setShowDelModal(false);
      setOpenDropdown(null);

      const res = await axios.delete(
        `${apiKey}/api/rms/admin/waste/${selectedWaste.id}`,
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );

      if (res.data.status) {
        // <-- backend returns "status", not "success"
        toast.success(res.data.message, {
          position: "bottom-right",
          theme: "colored",
        });
        setSelectedWaste(null);
        fetchWaste();
      } else {
        toast.error("Failed to delete waste.", {
          position: "bottom-right",
          theme: "colored",
        });
      }
    } catch (err) {
      toast.error("Failed to delete waste.", {
        position: "bottom-right",
        theme: "colored",
      });
      console.error(err);
    }
  };

  const [buttonData] = useState([
    {
      buttonText: languageHelper("Add Waste"),
      path: "addWaste",
    },
  ]);

  // Table columns
  const columns = useMemo(
    () => [
      { header: "SN", accessorKey: "SN" },
      { header: "Reference No", accessorKey: "reference_no" },
      { header: "Date", accessorKey: "date" },
      { header: "Total Loss", accessorKey: "total_loss" },
      { header: "Ingredient Count", accessorKey: "ingredient_count" },
      { header: "Food Menu Count", accessorKey: "food_menu_count" },
      { header: "Responsible Person", accessorKey: "responsible_person" },
      { header: "Note", accessorKey: "note" },
      {
        header: "Actions",
        accessorKey: "actions",
        cell: ({ row }) => (
          <div className="relative">
            <button
              className="text-blueGray-500 px-3 flex items-center justify-between gap-1 border rounded-full"
              onClick={() => toggleDropdown(row.original.id)}
            >
              <FaCog />
              <IoIosArrowDown />
            </button>

            {openDropdown === row.original.id && (
              <div className="absolute top-4 left-0 -translate-x-[75%] bg-white text-base z-10 py-2 list-none text-left rounded shadow-lg min-w-40">
                <Link
                  to={`addWaste/${row.original.id}`}
                  className="text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap text-blueGray-700 hover:bg-gray-100"
                >
                  <FaEdit /> Edit
                </Link>
                <button
                  onClick={() => openDeleteModal(row.original)}
                  className="text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap text-blueGray-700 hover:bg-gray-100"
                >
                  <FaTrashAlt /> Delete
                </button>
              </div>
            )}
          </div>
        ),
      },
    ],
    [openDropdown, toggleDropdown]
  );

  return (
    <main className="relative">
      <CardTable
        data={data}
        columns={columns}
        heading="Waste"
        buttonData={buttonData}
      />

      {/* Delete Modal */}
      {showDelModal && (
        <div
          className="w-full h-screen grid place-items-center fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-md p-4 z-10"
          onClick={() => setShowDelModal(false)}
        >
          <div
            className="p-4 rounded-lg bg-white max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className="text-[#774d96] font-semibold text-xl text-center">
              Alert!
            </h1>
            <p className="text-center text-gray-700">
              Are you sure you want to delete this waste record?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDelModal(false)}
                className="bg-gray-400 text-white px-4 py-1 rounded"
              >
                No
              </button>
              <button
                onClick={handleDelete}
                className="bg-d-cat-btn text-white px-4 py-1 rounded"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer />
    </main>
  );
}

export default Waste;
